import React from 'react'

import produce from 'immer'

import { DeliveryForecastDespatchAdviceItem } from '../../../../../data/models'
import {
  ComparisonArray,
  areItemsModified,
} from '../../../../../utils/helper/checkConfirmationCodes'
import { useConfigContext } from '../../../../ui-config'
import { requestPutDeliveryForecastDespatchAdviceItemById } from '../../../data'
import { useAllDeliveryForecastDespatchAdviceItemBatches } from '../../../data/hooks'
import { DataForArticleDto, DataForArticlePutDto } from '../../../data/model'
import { ArticleDetailFormForm } from './ArticleDetailForm.form'

type Props = {
  despatchAdviceId: string
  item: DeliveryForecastDespatchAdviceItem
  items: DeliveryForecastDespatchAdviceItem[]
  isLoading: boolean
  children?: JSX.Element
}

export const ArticleDetailFormData: React.FC<Props> = ({
  despatchAdviceId,
  item,
  items,
  isLoading,
  children,
}) => {
  const { uiConfig: config } = useConfigContext()
  const { data: batches, isLoading: isBatchesLoading } =
    useAllDeliveryForecastDespatchAdviceItemBatches(despatchAdviceId, item.Id)

  const getDisabledFieldIndexByName = (name: string): number => {
    const foundIndex = (item.Meta?.Fields?.DisabledCalculation || []).findIndex(
      p => p.Fieldname === name,
    )

    return foundIndex
  }

  const getDisabledFieldValueByName = (name: string): boolean => {
    const foundIndex = getDisabledFieldIndexByName(name)

    if (foundIndex < 0) return false

    return (
      (item.Meta?.Fields?.DisabledCalculation || [])[foundIndex].IsDisabled ||
      false
    )
  }

  const dataToFormValues = (
    data?: DeliveryForecastDespatchAdviceItem | undefined,
  ): DataForArticleDto => {
    return {
      desAdvPosition: data?.DesAdvPosition || '',
      buyerProductId: data?.BuyerProductId || '',
      buyerProductDescription: data?.BuyerProductDescription || '',
      confirmationItemId: data?.ConfirmationItemId || '',
      quantity: data?.Quantity || 0,
      openQuantity: data?.OpenQuantity || 0,
      confirmationQuantity: data?.ConfirmationQuantity || 0,
      deliveryStatus: data?.DeliveryStatus || 'NOT_SET',
      confirmationNetWeight: data?.ConfirmationNetWeight || 0,
      confirmationNetWeightChecked: getDisabledFieldValueByName(
        'ConfirmationNetWeight',
      ),
      confirmationNetWeightUnit: data?.ConfirmationNetWeightUnit || 'KG',
      confirmationCountryOfOrigin: data?.ConfirmationCountryOfOrigin || '',
      lastReceiving: data?.LastReceiving,
    }
  }

  const onFormSubmit = (formData: DataForArticlePutDto) => {
    submitDeliveryForecastDespatchAdviceItem(
      formData,
      despatchAdviceId,
      item,
      items,
    )
  }
  // const onFormSubmit = async (formData: DataForArticlePutDto) => {
  //   // Put for despatch advice item
  //   const newDeliveryForecastDespatchAdviceItem = produce(item, draft => {
  //     draft.ConfirmationQuantity = formData.confirmationQuantity
  //     draft.DeliveryStatus = formData.deliveryStatus
  //     draft.ConfirmationNetWeight = formData.confirmationNetWeight
  //     // draft.ConfirmationNetWeightChecked = formData.confirmationNetWeightChecked
  //     draft.ConfirmationNetWeightUnit = formData.confirmationNetWeightUnit
  //     draft.ConfirmationCountryOfOrigin = formData.confirmationCountryOfOrigin
  //     draft.ConfirmationCode = getConfirmationCode(formData)
  //   })

  //   await requestPutDeliveryForecastDespatchAdviceItemById(
  //     despatchAdviceId,
  //     item.Id,
  //     newDeliveryForecastDespatchAdviceItem,
  //   )

  //   // Patch for despatch advice
  //   const newDataArray: DeliveryForecastDespatchAdviceItem[] =
  //     items?.map(item =>
  //       item.Id === newDeliveryForecastDespatchAdviceItem.Id
  //         ? newDeliveryForecastDespatchAdviceItem
  //         : item,
  //     ) || []

  //   const patchData: PatchRequest<
  //     WebEdiDeliveryForecastDto<DeliveryForecastDespatchAdvice>
  //   > = [
  //     {
  //       op: 'replace',
  //       path: '/Payload/ConfirmationCode',
  //       value: isOrderModified(newDataArray)
  //         ? ('MODIFIED' as DeliveryForecastDespatchAdviceItem['ConfirmationCode'])
  //         : ('NOT_SET' as DeliveryForecastDespatchAdviceItem['ConfirmationCode']),
  //     },
  //   ]

  //   await requestPatchDeliveryForecastDespatchAdviceById(
  //     despatchAdviceId,
  //     patchData,
  //   )
  // }

  return (
    <ArticleDetailFormForm
      data={dataToFormValues(item)}
      config={config}
      onSubmit={onFormSubmit}
      isLoading={isLoading}
      hasBatches={!!batches?.length}
    >
      {children}
    </ArticleDetailFormForm>
  )
}

export async function submitDeliveryForecastDespatchAdviceItem(
  formData: DataForArticlePutDto,
  despatchAdviceId: string,
  item: DeliveryForecastDespatchAdviceItem,
  items: DeliveryForecastDespatchAdviceItem[],
) {
  const replaceDisabledFields = (name: string, value: boolean) => {
    const values = [...(item.Meta?.Fields?.DisabledCalculation || [])]
    const foundIndex = values.findIndex(p => p.Fieldname === name)
    if (foundIndex >= 0) {
      values.splice(foundIndex, 1)
    }

    values.push({ Fieldname: name, IsDisabled: value })
    return values
  }

  // Put for despatch advice item
  const newDeliveryForecastDespatchAdviceItem = produce(item, draft => {
    const newMeta = {
      Fields: {
        DisabledCalculation: replaceDisabledFields(
          'ConfirmationNetWeight',
          formData.confirmationNetWeightChecked,
        ),
      },
    }

    draft.ConfirmationQuantity = formData.confirmationQuantity
    draft.DeliveryStatus = formData.deliveryStatus
    draft.ConfirmationNetWeight = formData.confirmationNetWeight
    draft.ConfirmationNetWeightUnit = formData.confirmationNetWeightUnit
    draft.ConfirmationCountryOfOrigin = formData.confirmationCountryOfOrigin
    draft.ConfirmationCode = getConfirmationCode(formData, item)
    draft.Meta = { ...item.Meta, ...newMeta }
  })

  await requestPutDeliveryForecastDespatchAdviceItemById(
    despatchAdviceId,
    item.Id,
    newDeliveryForecastDespatchAdviceItem,
  )

  // Patch for despatch advice
  // const newDataArray: DeliveryForecastDespatchAdviceItem[] =
  //   items?.map(item =>
  //     item.Id === newDeliveryForecastDespatchAdviceItem.Id
  //       ? newDeliveryForecastDespatchAdviceItem
  //       : item,
  //   ) || []

  // const patchData: PatchRequest<
  //   WebEdiDeliveryForecastDto<DeliveryForecastDespatchAdvice>
  // > = [
  //   {
  //     op: 'replace',
  //     path: '/Payload/ConfirmationCode',
  //     value: isOrderModified(newDataArray)
  //       ? ('MODIFIED' as DeliveryForecastDespatchAdvice['ConfirmationCode'])
  //       : ('NOT_SET' as DeliveryForecastDespatchAdvice['ConfirmationCode']),
  //   },
  // ]

  // await requestPatchDeliveryForecastDespatchAdviceById(
  //   despatchAdviceId,
  //   patchData,
  // )
}

// check if items are modified
function getConfirmationCode(
  formData: DataForArticlePutDto,
  item: DeliveryForecastDespatchAdviceItem,
): DeliveryForecastDespatchAdviceItem['ConfirmationCode'] {
  const comparisionArray: ComparisonArray = [
    {
      order: item?.OpenQuantity,
      confirmation: formData.confirmationQuantity,
    },
  ]

  if (
    formData.confirmationQuantity === 0 &&
    formData.deliveryStatus === 'COMPLETE'
  ) {
    return 'REJECTED'
  }

  return areItemsModified(comparisionArray) ? 'MODIFIED' : 'ACCEPTED'
}
